import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaMobileAlt, FaHeart, FaKeyboard, FaTrophy, FaRedo, FaPlay, FaHome, FaStar } from 'react-icons/fa';
import { getCurrentSchool } from '../config/schools';

// Configuration du clavier AZERTY français avec les doigts assignés
const AZERTY_KEYBOARD = {
  // Rangée d'appui (home row) - la base de l'apprentissage
  homeRow: {
    keys: ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'],
    fingers: {
      'Q': { finger: 'auriculaire', hand: 'gauche', color: '#E74C3C' },
      'S': { finger: 'annulaire', hand: 'gauche', color: '#E67E22' },
      'D': { finger: 'majeur', hand: 'gauche', color: '#F1C40F' },
      'F': { finger: 'index', hand: 'gauche', color: '#2ECC71', isHome: true },
      'G': { finger: 'index', hand: 'gauche', color: '#2ECC71' },
      'H': { finger: 'index', hand: 'droite', color: '#3498DB' },
      'J': { finger: 'index', hand: 'droite', color: '#3498DB', isHome: true },
      'K': { finger: 'majeur', hand: 'droite', color: '#9B59B6' },
      'L': { finger: 'annulaire', hand: 'droite', color: '#E91E63' },
      'M': { finger: 'auriculaire', hand: 'droite', color: '#00BCD4' }
    }
  },
  // Rangée supérieure
  topRow: {
    keys: ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    fingers: {
      'A': { finger: 'auriculaire', hand: 'gauche', color: '#E74C3C' },
      'Z': { finger: 'annulaire', hand: 'gauche', color: '#E67E22' },
      'E': { finger: 'majeur', hand: 'gauche', color: '#F1C40F' },
      'R': { finger: 'index', hand: 'gauche', color: '#2ECC71' },
      'T': { finger: 'index', hand: 'gauche', color: '#2ECC71' },
      'Y': { finger: 'index', hand: 'droite', color: '#3498DB' },
      'U': { finger: 'index', hand: 'droite', color: '#3498DB' },
      'I': { finger: 'majeur', hand: 'droite', color: '#9B59B6' },
      'O': { finger: 'annulaire', hand: 'droite', color: '#E91E63' },
      'P': { finger: 'auriculaire', hand: 'droite', color: '#00BCD4' }
    }
  },
  // Rangée inférieure
  bottomRow: {
    keys: ['W', 'X', 'C', 'V', 'B', 'N'],
    fingers: {
      'W': { finger: 'auriculaire', hand: 'gauche', color: '#E74C3C' },
      'X': { finger: 'annulaire', hand: 'gauche', color: '#E67E22' },
      'C': { finger: 'majeur', hand: 'gauche', color: '#F1C40F' },
      'V': { finger: 'index', hand: 'gauche', color: '#2ECC71' },
      'B': { finger: 'index', hand: 'gauche', color: '#2ECC71' },
      'N': { finger: 'index', hand: 'droite', color: '#3498DB' }
    }
  }
};

// Leçons progressives - méthode dactylo professionnelle
const TYPING_LESSONS = [
  {
    level: 1,
    title: "Position de base",
    subtitle: "Les touches F et J",
    description: "Place tes index sur F (gauche) et J (droite). Ce sont les touches de repère avec une petite bosse tactile.",
    targetKeys: ['F', 'J'],
    planet: "Position",
    color: '#2ECC71',
    words: ['jf', 'fj', 'fjfj', 'jfjf', 'ff', 'jj', 'fjf', 'jfj', 'ffjj', 'jjff'],
    tip: "Tes index doivent toujours revenir sur F et J après chaque frappe !",
    wordsToWin: 10,
    speed: 4000
  },
  {
    level: 2,
    title: "Rangée d'appui - Index",
    subtitle: "Les touches F, G, H, J",
    description: "Tes index gèrent aussi G (gauche) et H (droite). Étends légèrement l'index vers le centre.",
    targetKeys: ['F', 'G', 'H', 'J'],
    planet: "Index",
    color: '#3498DB',
    words: ['gh', 'hg', 'fgh', 'jhg', 'gj', 'hf', 'fghj', 'jhgf', 'ghgh', 'fhjg'],
    tip: "L'index se déplace mais revient toujours à F ou J !",
    wordsToWin: 12,
    speed: 3800
  },
  {
    level: 3,
    title: "Rangée d'appui - Majeurs",
    subtitle: "Les touches D et K",
    description: "Tes majeurs restent sur D (gauche) et K (droite). Ils ne bougent presque jamais !",
    targetKeys: ['D', 'F', 'G', 'H', 'J', 'K'],
    planet: "Majeurs",
    color: '#9B59B6',
    words: ['dk', 'kd', 'fdk', 'jkd', 'dkfj', 'kjdf', 'dfk', 'kjd', 'dfjk', 'kjfd'],
    tip: "Les majeurs sont les doigts les plus stables !",
    wordsToWin: 14,
    speed: 3600
  },
  {
    level: 4,
    title: "Rangée d'appui - Annulaires",
    subtitle: "Les touches S et L",
    description: "Tes annulaires gèrent S (gauche) et L (droite). Ils sont plus faibles, patience !",
    targetKeys: ['S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    planet: "Annulaires",
    color: '#E91E63',
    words: ['sl', 'ls', 'sdf', 'lkj', 'sdfjkl', 'lkjfds', 'sfl', 'lks', 'dsl', 'klsf'],
    tip: "L'annulaire est timide, entraîne-le bien !",
    wordsToWin: 15,
    speed: 3400
  },
  {
    level: 5,
    title: "Rangée d'appui complète",
    subtitle: "Toutes les touches Q S D F G H J K L M",
    description: "Bravo ! Tu maîtrises la rangée d'appui. Tes auriculaires gèrent Q et M.",
    targetKeys: ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'],
    planet: "Appui",
    color: '#1B4965',
    // Mots français utilisant uniquement QSDFGHJKLM
    words: ['flash', 'fjdks', 'qsdfg', 'hjklm', 'dfjk', 'sklm', 'ghlm', 'qsd', 'mlkj', 'fdsq'],
    tip: "La rangée d'appui est ta base. Tes doigts y reviennent TOUJOURS !",
    wordsToWin: 16,
    speed: 3200
  },
  {
    level: 6,
    title: "Rangée supérieure - Voyelles",
    subtitle: "Les touches A, E, I, O, U",
    description: "Monte d'une rangée pour les voyelles. A (auriculaire G), E (majeur G), I (majeur D), O (annulaire D), U (index D).",
    targetKeys: ['A', 'E', 'I', 'O', 'U', 'F', 'J'],
    planet: "Voyelles",
    color: '#FFB703',
    // Mots avec voyelles
    words: ['aie', 'oui', 'eau', 'aou', 'oie', 'eue', 'aeu', 'iou', 'uia', 'oua'],
    tip: "Les voyelles sont essentielles. Monte puis redescends sur F et J !",
    wordsToWin: 14,
    speed: 3400
  },
  {
    level: 7,
    title: "Rangée supérieure complète",
    subtitle: "A Z E R T Y U I O P",
    description: "La rangée AZERTY complète ! R et T avec l'index gauche, Y et U avec l'index droit.",
    targetKeys: ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'F', 'J'],
    planet: "AZERTY",
    color: '#E63946',
    // Mots courts français
    words: ['art', 'rue', 'toi', 'roi', 'pou', 'air', 'riz', 'tri', 'pur', 'pot', 'pie', 'pre', 'pro'],
    tip: "AZERTY... c'est la rangée du haut de ton clavier français !",
    wordsToWin: 16,
    speed: 3000
  },
  {
    level: 8,
    title: "Rangée inférieure",
    subtitle: "W X C V B N",
    description: "Descends sous la rangée d'appui. W (auriculaire G), X (annulaire G), C (majeur G), V-B (index G), N (index D).",
    targetKeys: ['W', 'X', 'C', 'V', 'B', 'N', 'F', 'J'],
    planet: "Bas",
    color: '#8B4513',
    words: ['cv', 'bx', 'nb', 'vn', 'cx', 'wb', 'nv', 'bc', 'xv', 'wn', 'vb', 'nc'],
    tip: "La rangée du bas demande de plier les doigts. Garde les poignets droits !",
    wordsToWin: 14,
    speed: 3200
  },
  {
    level: 9,
    title: "Mots simples français",
    subtitle: "Combinaisons des 3 rangées",
    description: "Tu connais toutes les lettres ! Tapons des vrais mots français.",
    targetKeys: 'all',
    planet: "Mots",
    color: '#2ECC71',
    words: ['le', 'la', 'un', 'de', 'du', 'et', 'ou', 'je', 'tu', 'il', 'on', 'en', 'au', 'ce', 'ne', 'se', 'me', 'te'],
    tip: "Chaque doigt a sa zone. Ne regarde pas le clavier !",
    wordsToWin: 18,
    speed: 2800
  },
  {
    level: 10,
    title: "Vocabulaire journalistique",
    subtitle: "Les mots du reporter",
    description: "Un reporter tape vite et bien ! Voici des mots essentiels du journalisme.",
    targetKeys: 'all',
    planet: "Presse",
    color: '#1B4965',
    words: ['info', 'fait', 'actu', 'news', 'sujet', 'angle', 'titre', 'lead', 'source', 'photo', 'video', 'audio', 'live', 'web'],
    tip: "Un bon journaliste tape sans regarder son clavier !",
    wordsToWin: 20,
    speed: 2600
  },
  {
    level: 11,
    title: "Défi vitesse !",
    subtitle: "Protège Mediapolis !",
    description: "Les planètes ennemies attaquent ! Tape vite pour sauver la cité des médias !",
    targetKeys: 'all',
    planet: "FakeNews",
    color: '#E63946',
    words: ['mensonge', 'rumeur', 'hoax', 'intox', 'canular', 'fake', 'faux', 'erreur', 'piege', 'virus'],
    tip: "Vitesse + Précision = Super Reporter !",
    wordsToWin: 25,
    speed: 2200
  }
];

// Composant clavier visuel
function VisualKeyboard({ targetKeys, currentLetter, allFingers }) {
  const rows = [
    { keys: ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'], row: 'topRow' },
    { keys: ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'], row: 'homeRow' },
    { keys: ['W', 'X', 'C', 'V', 'B', 'N'], row: 'bottomRow' }
  ];

  const getKeyInfo = (key) => {
    for (const rowName of ['homeRow', 'topRow', 'bottomRow']) {
      if (AZERTY_KEYBOARD[rowName].fingers[key]) {
        return AZERTY_KEYBOARD[rowName].fingers[key];
      }
    }
    return null;
  };

  const isTargetKey = (key) => {
    if (targetKeys === 'all') return true;
    return targetKeys.includes(key);
  };

  return (
    <div className="bg-gray-900 rounded-xl p-4 border-4 border-gray-700">
      <div className="space-y-2">
        {rows.map((rowData, rowIdx) => (
          <div key={rowIdx} className="flex justify-center gap-1">
            {rowIdx === 2 && <div className="w-8" />}
            {rowData.keys.map((key) => {
              const keyInfo = getKeyInfo(key);
              const isTarget = isTargetKey(key);
              const isCurrent = currentLetter?.toUpperCase() === key;
              const isHomeKey = keyInfo?.isHome;
              
              return (
                <motion.div
                  key={key}
                  animate={isCurrent ? { scale: [1, 1.2, 1], boxShadow: '0 0 20px #FFD700' } : {}}
                  transition={{ duration: 0.3 }}
                  className={`
                    w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg
                    border-2 transition-all duration-200
                    ${isCurrent 
                      ? 'bg-yellow-400 text-black border-yellow-300 scale-110' 
                      : isTarget 
                        ? 'border-white/50' 
                        : 'border-gray-700 opacity-40'
                    }
                    ${isHomeKey ? 'border-b-4' : ''}
                  `}
                  style={{ 
                    backgroundColor: isCurrent ? '#FFD700' : isTarget ? keyInfo?.color + '80' : '#374151',
                    borderBottomColor: isHomeKey ? '#FFD700' : undefined
                  }}
                >
                  {key}
                </motion.div>
              );
            })}
          </div>
        ))}
        {/* Barre espace */}
        <div className="flex justify-center mt-2">
          <div className="w-64 h-8 bg-gray-700 rounded-lg border-2 border-gray-600 flex items-center justify-center text-gray-400 text-sm">
            ESPACE (pouces)
          </div>
        </div>
      </div>
      
      {/* Légende des doigts */}
      <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ background: '#E74C3C' }}></span>
          <span className="text-gray-400">Auriculaire G</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ background: '#E67E22' }}></span>
          <span className="text-gray-400">Annulaire G</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ background: '#F1C40F' }}></span>
          <span className="text-gray-400">Majeur G</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ background: '#2ECC71' }}></span>
          <span className="text-gray-400">Index G</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ background: '#3498DB' }}></span>
          <span className="text-gray-400">Index D</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ background: '#9B59B6' }}></span>
          <span className="text-gray-400">Majeur D</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ background: '#E91E63' }}></span>
          <span className="text-gray-400">Annulaire D</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ background: '#00BCD4' }}></span>
          <span className="text-gray-400">Auriculaire D</span>
        </div>
      </div>
    </div>
  );
}

function TypingGamePage() {
  const navigate = useNavigate();
  const [gameStarted, setGameStarted] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [words, setWords] = useState([]);
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [victory, setVictory] = useState(false);
  const [wordsTyped, setWordsTyped] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [explosions, setExplosions] = useState([]);
  const [showIntro, setShowIntro] = useState(true);
  const [accuracy, setAccuracy] = useState(100);
  const [totalKeystrokes, setTotalKeystrokes] = useState(0);
  const [correctKeystrokes, setCorrectKeystrokes] = useState(0);
  const inputRef = useRef(null);
  const gameContainerRef = useRef(null);

  useEffect(() => {
    const checkMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsMobile(checkMobile);
  }, []);

  // Générer les mots qui arrivent
  useEffect(() => {
    if (gameStarted && !gameOver && !victory && !showIntro) {
      const level = TYPING_LESSONS[currentLevel];
      const interval = setInterval(() => {
        if (words.length < 5) {
          const randomWord = level.words[Math.floor(Math.random() * level.words.length)];
          const angle = Math.random() * 360;
          const distance = 42;
          const newWord = {
            id: Date.now() + Math.random(),
            text: randomWord,
            angle: angle,
            distance: distance,
            speed: 0.15 + Math.random() * 0.1
          };
          setWords(prev => [...prev, newWord]);
        }
      }, level.speed);

      return () => clearInterval(interval);
    }
  }, [gameStarted, currentLevel, words.length, gameOver, victory, showIntro]);

  // Mouvement des mots
  useEffect(() => {
    if (gameStarted && !gameOver && !victory && !showIntro) {
      const moveInterval = setInterval(() => {
        setWords(prev => {
          const updated = prev.map(word => ({
            ...word,
            distance: word.distance - word.speed
          }));

          // Vérifier collision avec la planète
          const hitPlanet = updated.filter(w => w.distance <= 5);
          if (hitPlanet.length > 0) {
            // Créer explosion pour chaque mot qui touche
            hitPlanet.forEach(() => {
              setExplosions(prevExp => [...prevExp, { 
                id: Date.now() + Math.random(), 
                x: 50, 
                y: 50 
              }]);
            });
            
            // Retirer les explosions après l'animation
            setTimeout(() => {
              setExplosions(prevExp => prevExp.slice(hitPlanet.length));
            }, 800);

            // Réduire les vies
            setLives(l => {
              const newLives = Math.max(0, l - hitPlanet.length);
              if (newLives <= 0) {
                setGameOver(true);
                setGameStarted(false);
              }
              return newLives;
            });

            return updated.filter(w => w.distance > 5);
          }

          return updated;
        });
      }, 50);

      return () => clearInterval(moveInterval);
    }
  }, [gameStarted, gameOver, victory, showIntro]);

  // Gestion de l'input avec support du backspace
  const handleInput = (e) => {
    const value = e.target.value.toLowerCase();
    const prevValue = input;
    setInput(value);
    
    // Ne compter que les ajouts de caractères (pas le backspace)
    if (value.length > prevValue.length) {
      setTotalKeystrokes(t => t + 1);
      
      // Vérifier si la frappe correspond au début d'un mot
      const matchingWord = words.find(w => w.text.toLowerCase().startsWith(value));
      if (matchingWord) {
        setCorrectKeystrokes(c => c + 1);
      }
    }

    // Chercher une correspondance exacte
    const matchedWord = words.find(w => w.text.toLowerCase() === value);
    if (matchedWord) {
      // Succès ! Mot détruit
      const wordX = 50 + Math.cos(matchedWord.angle * Math.PI / 180) * matchedWord.distance;
      const wordY = 50 + Math.sin(matchedWord.angle * Math.PI / 180) * matchedWord.distance;
      
      setExplosions(prev => [...prev, { id: Date.now(), x: wordX, y: wordY }]);
      setTimeout(() => setExplosions(prev => prev.slice(1)), 600);
      
      setWords(prev => prev.filter(w => w.id !== matchedWord.id));
      setScore(s => s + matchedWord.text.length * 10);
      setWordsTyped(w => w + 1);
      setInput('');

      // Vérifier victoire niveau
      const level = TYPING_LESSONS[currentLevel];
      if (wordsTyped + 1 >= level.wordsToWin) {
        if (currentLevel < TYPING_LESSONS.length - 1) {
          // Niveau suivant
          setTimeout(() => {
            setCurrentLevel(c => c + 1);
            setWordsTyped(0);
            setWords([]);
            setShowIntro(true);
          }, 1000);
        } else {
          // Victoire totale!
          setTimeout(() => {
            setVictory(true);
            setGameStarted(false);
          }, 1000);
        }
      }
    }
  };
  
  // Calculer la précision (mise à jour automatique)
  useEffect(() => {
    if (totalKeystrokes > 0) {
      setAccuracy(Math.round((correctKeystrokes / totalKeystrokes) * 100));
    }
  }, [totalKeystrokes, correctKeystrokes]);

  // Gestion des touches spéciales - le backspace fonctionne naturellement avec l'input React

  const startGame = () => {
    setGameStarted(true);
    setGameOver(false);
    setVictory(false);
    setLives(3);
    setScore(0);
    setWordsTyped(0);
    setWords([]);
    setInput('');
    setCurrentLevel(0);
    setShowIntro(true);
    setAccuracy(100);
    setTotalKeystrokes(0);
    setCorrectKeystrokes(0);
    setExplosions([]);
  };

  const startLevel = () => {
    setShowIntro(false);
    setWords([]);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const level = TYPING_LESSONS[currentLevel];

  // Déterminer la prochaine lettre à taper
  const getNextLetter = () => {
    if (!words.length || !input) return null;
    // Trouver le mot le plus proche
    const closestWord = [...words].sort((a, b) => a.distance - b.distance)[0];
    if (closestWord && input.length < closestWord.text.length) {
      return closestWord.text[input.length];
    }
    return null;
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)' }}>
      {/* Header */}
      <header className="border-b-4 border-white/20 bg-black/50 backdrop-blur relative z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="comic-button bg-white text-black text-sm px-3 py-2"
            data-testid="back-button"
          >
            <FaArrowLeft className="inline mr-1" /> Retour
          </button>
          <h1 className="text-xl md:text-2xl comic-title text-white flex items-center">
            <FaKeyboard className="mr-2" /> Dactylo AZERTY
          </h1>
          <div className="text-white text-right text-sm">
            <p className="comic-text">Score: <span className="text-yellow-400">{score}</span></p>
            <p className="text-xs text-gray-400">Précision: {accuracy}%</p>
          </div>
        </div>
      </header>

      <div className="relative min-h-[calc(100vh-70px)] overflow-hidden" ref={gameContainerRef}>
        {/* Avertissement mobile */}
        {isMobile && (
          <motion.div 
            className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50"
            initial={{ y: -100 }}
            animate={{ y: 0 }}
          >
            <div className="comic-panel bg-yellow-400 border-black p-3 max-w-sm">
              <FaMobileAlt className="inline text-xl mr-2" />
              <span className="font-bold text-sm">Ce jeu nécessite un clavier AZERTY physique !</span>
            </div>
          </motion.div>
        )}

        {/* Écran d'accueil */}
        {!gameStarted && !gameOver && !victory && (
          <div className="absolute inset-0 flex items-center justify-center z-40 p-4">
            <motion.div
              initial={{ scale: 0, rotate: -5 }}
              animate={{ scale: 1, rotate: 0 }}
              className="comic-panel max-w-4xl text-center"
            >
              <div className="onomatopoeia text-4xl md:text-5xl mb-4 text-blue-500">
                COURS DE DACTYLO AZERTY
              </div>
              
              <p className="text-lg mb-4 comic-text">
                Apprends à taper à 10 doigts sur un clavier français !
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs">
                {TYPING_LESSONS.slice(0, 8).map((lvl, idx) => (
                  <div key={idx} className="p-2 rounded-lg" style={{ background: lvl.color + '30', border: `2px solid ${lvl.color}` }}>
                    <div className="font-bold" style={{ color: lvl.color }}>{lvl.level}. {lvl.title}</div>
                  </div>
                ))}
              </div>
              
              <div className="bg-gray-100 rounded-lg p-4 mb-4 text-left max-w-lg mx-auto">
                <h3 className="font-bold mb-2 flex items-center"><FaKeyboard className="mr-2" /> Méthode pédagogique</h3>
                <ul className="text-sm space-y-1 text-gray-700">
                  <li>✅ Position de base : index sur <strong>F</strong> et <strong>J</strong></li>
                  <li>✅ Chaque doigt a ses touches assignées</li>
                  <li>✅ Progression : rangée d'appui → haut → bas</li>
                  <li>✅ Ne regarde JAMAIS le clavier !</li>
                </ul>
              </div>
              
              <button
                onClick={startGame}
                className="comic-button comic-button-navy text-2xl py-3 px-8"
                data-testid="start-game-button"
              >
                <FaPlay className="inline mr-2" /> COMMENCER L'ENTRAÎNEMENT
              </button>
            </motion.div>
          </div>
        )}

        {/* Introduction du niveau */}
        {gameStarted && showIntro && !gameOver && !victory && (
          <div className="absolute inset-0 flex items-center justify-center z-40 p-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="comic-panel max-w-3xl text-center"
            >
              <div 
                className="inline-block px-6 py-2 rounded-full mb-4"
                style={{ background: level.color }}
              >
                <span className="text-white comic-title text-xl">Niveau {level.level}</span>
              </div>
              
              <h2 className="comic-title text-3xl mb-2" style={{ color: level.color }}>
                {level.title}
              </h2>
              <p className="text-lg text-gray-600 mb-4">{level.subtitle}</p>
              
              <div className="bg-gray-100 rounded-xl p-4 mb-4">
                <p className="comic-text text-base">{level.description}</p>
              </div>
              
              {/* Clavier visuel */}
              <div className="mb-4">
                <VisualKeyboard 
                  targetKeys={level.targetKeys} 
                  currentLetter={null}
                />
              </div>
              
              <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg p-3 mb-4">
                <p className="text-yellow-800 font-bold">💡 {level.tip}</p>
              </div>
              
              <p className="text-sm text-gray-500 mb-4">
                Objectif : {level.wordsToWin} mots à taper
              </p>
              
              <button
                onClick={startLevel}
                className="comic-button text-xl py-3 px-8 text-white"
                style={{ background: level.color }}
                data-testid="start-level-button"
              >
                C'EST PARTI !
              </button>
            </motion.div>
          </div>
        )}

        {/* Game Over */}
        {gameOver && (
          <div className="absolute inset-0 flex items-center justify-center z-40 bg-black/90 p-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="comic-panel max-w-xl text-center"
            >
              <div className="text-6xl mb-4">💥</div>
              <h2 className="comic-title text-3xl mb-4 text-red-600">
                Mediapolis a besoin de toi !
              </h2>
              <div className="space-y-2 mb-4">
                <p className="text-xl">Score: <strong>{score}</strong></p>
                <p>Mots tapés: <strong>{wordsTyped}</strong></p>
                <p>Niveau atteint: <strong>{level.level} - {level.title}</strong></p>
                <p>Précision: <strong>{accuracy}%</strong></p>
              </div>
              <div className="speech-bubble mb-4 bg-blue-50">
                <p className="comic-text">
                  Continue à t'entraîner ! La clé, c'est la pratique régulière. 15 minutes par jour suffisent ! 💪
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={startGame}
                  className="comic-button comic-button-navy text-lg py-2 px-4"
                  data-testid="restart-button"
                >
                  <FaRedo className="inline mr-1" /> Recommencer
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="comic-button bg-gray-200 text-lg py-2 px-4"
                >
                  <FaHome className="inline mr-1" /> Retour
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Victory */}
        {victory && (
          <div className="absolute inset-0 flex items-center justify-center z-40 bg-black/90 p-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="comic-panel max-w-xl text-center"
            >
              <motion.div 
                className="text-6xl mb-4"
                animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                🏆
              </motion.div>
              <h2 className="comic-title text-3xl mb-4 text-green-600">
                FÉLICITATIONS !
              </h2>
              <p className="text-xl mb-2">Tu maîtrises le clavier AZERTY !</p>
              <div className="space-y-2 mb-4">
                <p className="text-2xl">Score final: <strong className="text-yellow-500">{score}</strong></p>
                <p>Mots tapés: <strong>{wordsTyped}</strong></p>
                <p>Précision: <strong>{accuracy}%</strong></p>
              </div>
              <div className="speech-bubble mb-4 bg-green-50">
                <p className="comic-text">
                  Tu tapes comme un vrai journaliste professionnel ! Continue à t'entraîner pour gagner en vitesse. 🌟
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={startGame}
                  className="comic-button comic-button-navy text-lg py-2 px-4"
                  data-testid="play-again-button"
                >
                  <FaRedo className="inline mr-1" /> Rejouer
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="comic-button bg-green-500 text-white text-lg py-2 px-4"
                >
                  <FaHome className="inline mr-1" /> Dashboard
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Jeu actif */}
        {gameStarted && !showIntro && !gameOver && !victory && (
          <>
            {/* Info niveau */}
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-30 text-center">
              <div
                className="comic-badge text-lg px-4 py-2 mb-1"
                style={{ background: level.color, color: 'white' }}
              >
                Niveau {level.level}: {level.title}
              </div>
              <div className="flex justify-center gap-4 text-white text-sm">
                <span className="flex items-center">
                  <FaHeart className="text-red-500 mr-1" />
                  {'❤️'.repeat(Math.max(0, lives))}
                </span>
                <span>Mots: {wordsTyped}/{level.wordsToWin}</span>
                <span className="flex items-center">
                  <FaStar className="text-yellow-400 mr-1" />
                  {accuracy}%
                </span>
              </div>
            </div>

            {/* Zone de jeu avec planète */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ marginTop: '60px' }}>
              {/* Planète centrale */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                className="absolute"
                style={{ zIndex: 10 }}
              >
                <div 
                  className="w-28 h-28 md:w-36 md:h-36 rounded-full flex items-center justify-center"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #62B6CB 0%, #1B4965 60%, #0a1929 100%)',
                    boxShadow: '0 0 40px rgba(74, 144, 226, 0.6)'
                  }}
                >
                  <span className="text-white font-bold text-xs md:text-sm text-center px-2">MEDIAPOLIS</span>
                </div>
              </motion.div>

              {/* Mots en orbite */}
              <AnimatePresence>
                {words.map(word => {
                  const x = 50 + Math.cos(word.angle * Math.PI / 180) * word.distance;
                  const y = 50 + Math.sin(word.angle * Math.PI / 180) * word.distance;
                  
                  // Vérifier si le mot commence par l'input actuel
                  const isMatching = input && word.text.toLowerCase().startsWith(input.toLowerCase());
                  
                  return (
                    <motion.div
                      key={word.id}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 2 }}
                      className="absolute"
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: 5
                      }}
                    >
                      <div 
                        className={`px-3 py-1 md:px-4 md:py-2 border-3 font-bold text-base md:text-xl rounded-lg ${isMatching ? 'ring-4 ring-green-400' : ''}`}
                        style={{
                          background: isMatching ? '#10B981' : level.color,
                          color: 'white',
                          borderColor: isMatching ? '#34D399' : 'black',
                          fontFamily: 'monospace'
                        }}
                      >
                        {isMatching ? (
                          <>
                            <span className="text-yellow-300">{word.text.slice(0, input.length)}</span>
                            <span>{word.text.slice(input.length)}</span>
                          </>
                        ) : (
                          word.text
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {/* Explosions */}
              <AnimatePresence>
                {explosions.map(explosion => (
                  <motion.div
                    key={explosion.id}
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{ scale: 3, opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    className="absolute text-5xl"
                    style={{
                      left: `${explosion.x}%`,
                      top: `${explosion.y}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 20
                    }}
                  >
                    💥
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Zone de saisie et clavier */}
            <div className="absolute bottom-0 left-0 right-0 z-30 p-4 bg-gradient-to-t from-black/80 to-transparent">
              {/* Input */}
              <div className="max-w-md mx-auto mb-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={handleInput}
                  className="w-full text-2xl text-center font-mono py-3 px-4 rounded-xl border-4 border-white/50 bg-white text-black"
                  placeholder="Tape ici..."
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-testid="typing-input"
                />
              </div>
              
              {/* Mini clavier visuel */}
              <div className="max-w-xl mx-auto hidden md:block">
                <VisualKeyboard 
                  targetKeys={level.targetKeys} 
                  currentLetter={getNextLetter()}
                />
              </div>
            </div>
          </>
        )}

        {/* Étoiles de fond */}
        {[...Array(60)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute bg-white rounded-full"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.5 + 0.2
            }}
            animate={{ opacity: [0.2, 0.8, 0.2] }}
            transition={{ duration: Math.random() * 3 + 2, repeat: Infinity }}
          />
        ))}
      </div>
    </div>
  );
}

export default TypingGamePage;
